import React, { useEffect, useState } from 'react';
import FormView from '../../components/views/formView';
import { Grid, Box } from '@mui/material';
import FieldText from '../../components/general/fields/fieldText';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { USEREDIT, USERS } from '../../../routes/paths';
import FieldSelect from '../../components/general/fields/fieldSelect';
import { RootState } from '../../../redux/store';
import { SwalCustomClass } from '../../components/general/toastSwals/swalCustomClass';
import { UsersMessages } from '../../../shared/messages/usersMessages';
import {
    asyncPatchtUser,
    asyncPostUser,
} from '../../../services/users.service';
import { ISelect } from '../../../shared/types/shared';
import { asyncGetProjects } from '../../../services/projects.service';
import FieldMultipleSelect from '../../components/general/fields/fieldMultipleSelect';
import { isAdminUser } from '../../../shared/utils/user';

type Props = {
    idUser?: any;
    user?: any;
    onSave?: any;
};

const UserForm = ({ user, idUser, onSave }: Props) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const { id: idUserLog } = logUser || {};
    const { usersMessages }: any = UsersMessages;
    const [projects, setProjects] = useState<ISelect[]>([]);

    const initialProjects: any[] = [];

    user?.projects &&
        Array.isArray(user?.projects) &&
        user?.projects.map((project: ISelect) =>
            initialProjects.push(project?.id)
        );

    const formik = useFormik({
        initialValues: {
            document: user !== undefined ? user.document : '',
            name: user !== undefined ? user.name : '',
            surname: user !== undefined ? user.surname : '',
            username: user !== undefined ? user.username : '',
            password: '',
            password_retry: '',
            type: user !== undefined ? user.type : '',
            status: user !== undefined ? user.status : '',
            projects: user !== undefined ? initialProjects : [],
        },
        validationSchema: Yup.object({
            document: Yup.number()
                .typeError('El documento debe ser un número')
                .required('El documento es obligatorio'),
            name: Yup.string().required('El nombre es obligatorio'),
            surname: Yup.string().required('El apellido es obligatorio'),
            username: Yup.string()
                .email('El correo es invalido')
                .required('El correo de usuario es obligatorio')
                .test('Validate Email', 'El correo es inválido', (value) => {
                    const re =
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
                    return re.test(String(value).toLowerCase());
                }),
            password: Yup.string().when({
                is: () => Boolean(idUser === undefined),
                then: (schema) =>
                    schema
                        .required('La contraseña es obligatoria')
                        .min(
                            8,
                            'La contraseña debe tener al menos 8 caracteres'
                        ),
            }),
            password_retry: Yup.string().when(['password'], {
                is: (password: any) =>
                    Boolean(idUser === undefined) || password !== undefined,
                then: (schema) =>
                    schema
                        .required(
                            'La confirmación de la contraseña es obligatoria'
                        )
                        .oneOf(
                            [Yup.ref('password')],
                            'Las contraseñas no coinciden'
                        ),
            }),
            type: Yup.string().required('El perfil es obligatorio'),
        }),
        onSubmit: async (formUser) => {
            if (idUser !== undefined) {
                asyncPatchtUser(idUser, formUser).then((body: any) => {
                    if (body?.items !== undefined) {
                        onSave(true);
                        navigate(`${USEREDIT}/${body.items.id}`);
                        SwalCustomClass.fire({
                            text: usersMessages.MESSAGE_EDIT,
                            icon: 'success',
                        });
                    } else if (body?.errors !== undefined) {
                        SwalCustomClass.fire({
                            text: usersMessages[body.message],
                            icon: 'error',
                        });
                    }
                });
            } else {
                const body: any = await asyncPostUser(formUser);
                if (body?.items !== undefined) {
                    navigate(`${USEREDIT}/${body.items.id}`);
                    SwalCustomClass.fire({
                        text: usersMessages.MESSAGE_NEW,
                        icon: 'success',
                    });
                } else if (body?.errors !== undefined) {
                    SwalCustomClass.fire({
                        text: usersMessages[body.message],
                        icon: 'error',
                    });
                }
            }
        },
    });

    const profiles: ISelect[] = [
        {
            id: 2,
            name: 'user',
        },
        {
            id: 3,
            name: 'file manager',
        },
    ];

    if (isAdminUser(type)) {
        profiles.push(
            {
                id: 4,
                name: 'manager',
            },
            {
                id: 5,
                name: 'admin security',
            }
        );
    }

    useEffect(() => {
        asyncGetProjects().then((projects) =>
            setProjects(projects.items as ISelect[])
        );
    }, []);

    return (
        <>
            <FormView
                title={
                    idUser !== undefined
                        ? usersMessages.TITLE_EDIT
                        : usersMessages.TITLE_NEW
                }
                onClickSave={formik.handleSubmit}
                onClickBack={() => navigate(USERS)}
            />
            <Box mt={3} px={2}>
                <Grid
                    container
                    spacing={{ xs: 1.5, md: 1.5 }}
                    columnSpacing={{ xs: 1.5, sm: 1.5, md: 1.5 }}
                >
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 1.9 : 2}
                        lg={idUserLog === idUser ? 1.9 : 2}
                    >
                        <FieldText
                            label="Código"
                            name="document"
                            size="small"
                            required={true}
                            touched={formik.touched.document}
                            errors={formik.errors.document}
                            value={formik.values.document}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.3 : 3.5}
                        lg={idUserLog === idUser ? 3.3 : 3.5}
                    >
                        <FieldText
                            label="Nombre"
                            name="name"
                            size="small"
                            required={true}
                            touched={formik.touched.name}
                            errors={formik.errors.name}
                            value={formik.values.name}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.3 : 3.5}
                        lg={idUserLog === idUser ? 3.3 : 3.5}
                    >
                        <FieldText
                            label="Apellido"
                            name="surname"
                            size="small"
                            required={true}
                            touched={formik.touched.surname}
                            errors={formik.errors.surname}
                            value={formik.values.surname}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    {idUserLog !== idUser ? (
                        <Grid item xs={12} md={3} lg={3}>
                            <FieldSelect
                                data={profiles}
                                label="Perfil"
                                name="type"
                                size="small"
                                required={true}
                                defineValue="value"
                                touched={formik.touched.type}
                                errors={formik.errors.type}
                                value={formik.values.type}
                                onChange={formik.handleChange}
                            />
                        </Grid>
                    ) : null}
                    <Grid
                        item
                        xs={12}
                        md={idUserLog === idUser ? 3.5 : 3}
                        lg={idUserLog === idUser ? 3.5 : 3}
                    >
                        <FieldText
                            label="Correo Usuario"
                            name="username"
                            size="small"
                            required={true}
                            touched={formik.touched.username}
                            errors={formik.errors.username}
                            value={formik.values.username}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} lg={3}>
                        <FieldMultipleSelect
                            data={projects}
                            label="Proyectos"
                            name="projects"
                            size="small"
                            defineValue="id"
                            touched={formik.touched.projects}
                            errors={formik.errors.projects}
                            value={formik.values.projects}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} lg={3}>
                        <FieldText
                            label="Contraseña"
                            name="password"
                            type="password"
                            size="small"
                            required={true}
                            touched={formik.touched.password}
                            errors={formik.errors.password}
                            value={formik.values.password}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                    <Grid item xs={12} md={3} lg={3}>
                        <FieldText
                            label="Confirmar Contraseña"
                            name="password_retry"
                            type="password"
                            size="small"
                            required={true}
                            touched={formik.touched.password_retry}
                            errors={formik.errors.password_retry}
                            value={formik.values.password_retry}
                            onChange={formik.handleChange}
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

export default UserForm;
